import React from 'react';
import { PipelineState } from 'src/pages/Settings/components/WalkthroughBackupUploader/Hooks/useWalkthroughBackupUpload';
import './StepIndicator.scss';

interface StepConfig {
  label: string;
  activeStates: PipelineState[];
  completeStates: PipelineState[];
}

const STEPS: StepConfig[] = [
  {
    label: 'Validating',
    activeStates: ['validating', 'error-invalid-json', 'error-missing-fields'],
    completeStates: [
      'checking-status',
      'file-preview',
      'fetching-url',
      'uploading',
      'success',
      'error-not-eligible',
      'error-network',
      'error-upload-failed',
    ],
  },
  {
    label: 'Pre-flight',
    activeStates: ['checking-status', 'error-not-eligible'],
    completeStates: [
      'file-preview',
      'fetching-url',
      'uploading',
      'success',
      'error-upload-failed',
    ],
  },
  {
    label: 'Presigned URL',
    activeStates: ['fetching-url'],
    completeStates: ['uploading', 'success', 'error-upload-failed'],
  },
  {
    label: 'Uploading',
    activeStates: ['uploading', 'error-upload-failed'],
    completeStates: ['success'],
  },
  {
    label: 'Complete',
    activeStates: ['success'],
    completeStates: ['success'],
  },
];

const HIDDEN_STEP_LABELS = ['Presigned URL'];

type StepStatus = 'pending' | 'active' | 'complete' | 'error';

interface StepDotProps {
  status: StepStatus;
  label: string;
}

const StepDot = ({ status, label }: StepDotProps) => (
  <div className="si_step">
    <div className={`si_dot si_dot--${status}`}>
      {status === 'complete' && <span className="si_dot_check">✓</span>}
      {status === 'error' && <span className="si_dot_cross">✕</span>}
    </div>
    <span className={`si_label si_label--${status}`}>{label}</span>
  </div>
);

interface ConnectorProps {
  active: boolean;
}

const Connector = ({ active }: ConnectorProps) => (
  <div className={`si_connector ${active ? 'si_connector--active' : ''}`} />
);

interface StepIndicatorProps {
  currentState: PipelineState;
}

const isErrorState = (state: PipelineState): boolean =>
  state.startsWith('error');

export const StepIndicator = ({ currentState }: StepIndicatorProps) => {

  const getStepStatus = (step: StepConfig): StepStatus => {
    if (step.completeStates.includes(currentState)) return 'complete';

    if (
      isErrorState(currentState) &&
      step.activeStates.includes(currentState)
    ) {
      return 'error';
    }

    if (step.activeStates.includes(currentState)) return 'active';
    return 'pending';
  };

  return (
    <div className="si_container">
      {STEPS.map((step: StepConfig, index: number) => {
        const status: StepStatus = getStepStatus(step);
        const isLast: boolean = index === STEPS.length - 1;

        const nextComplete: boolean =
          !isLast && STEPS[index + 1].completeStates.includes(currentState);

        const isHidden: boolean = HIDDEN_STEP_LABELS.includes(step.label);

        if (isHidden) return null;

        return (
          <div key={step.label} className="si_step_wrapper">
            <StepDot status={status} label={step.label} />
            {!isLast && <Connector active={nextComplete} />}
          </div>
        );
      })}
    </div>
  );
};
